import React from "react";
import PropTypes from "prop-types";
import { Card, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Flex from "components/common/Flex";
import { PieChart } from "echarts/charts";
import * as echarts from "echarts/core";
import BasicECharts from "components/common/BasicEChart";
import {
  GridComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useAppContext } from "providers/AppProvider";
import { useSelector } from "react-redux";

// Register ECharts components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer,
]);


const defaultRadius = ["50%", "70%"];
const defaultRate = "0%";

// ECharts options
const getOptions = (getThemeColor, data, isDark) => ({
  color: data.map((item) => item.color),
  tooltip: {
    trigger: 'item',
    padding: 0,
    backgroundColor: 'transparent',
    borderWidth: 0,
    transitionDuration: 0,
    formatter: (params) => {
      const bgColor = isDark ? '#0b1727' : '#F2F6FC';
      const borderColor = isDark ? 'rgba(255, 255, 255, 0.15)' : '#edf2f9';
      const textColor = isDark ? '#f8fafc' : '#4d5969';

      return `
        <div class="p-2 px-3 rounded-2 shadow-sm" style="background-color: ${bgColor}; border: 1px solid ${borderColor}; min-width: 200px;">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <div class="d-flex align-items-center gap-2">
              <div class="rounded-circle" style="width: 8px; height: 8px; background-color: ${params.color}"></div>
              <span class="fw-semi-bold fs-11" style="color: ${textColor}">${params.name}</span>
            </div>
            </div>
            <span style="font-size:12px; color: ${textColor}">
              ${params.percent}%
            </span>
          
        </div>
      `;
    }
  },
  series: [
    {
      name: "Eligibility",
      type: "pie",
      radius: ["65%", "85%"],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 4,
        borderColor: getThemeColor("gray-100"),
        borderWidth: 2
      },
      label: {
        show: false,
        position: "center",
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.15)'
        }
      },
      data,
    },
  ],
});



// Main Component
const TotalRateEligibility = () => {
  const { generalDashboardData } = useSelector((state) => state.dashboard);
  const { getThemeColor, config: { isDark } } = useAppContext();

  // Extract eligibility data or use defaults
  const eligibility = generalDashboardData?.eligibility || {
    total: 0,
    eligibile: 0,
    invalid: 0,
    noresponse: 0,
    ineligible: 0,
  };

  const data = [
    {
      name: "Eligible",
      value: eligibility.eligibile || 0,
      color: "#10b981", // Green
    },
    {
      name: "Ineligible",
      value: eligibility.ineligible || 0,
      color: "#ef4444", // Red
    },
    {
      name: "Invalid",
      value: eligibility.invalid || 0,
      color: "#f59e0b", // Orange
    },
    {
      name: "No Response",
      value: eligibility.noresponse || 0,
      color: "#64748b" // Gray
    },
  ];

  const total = eligibility.total || data.reduce((acc, val) => acc + val.value, 0);

  return (
    <Col xs={12} md={6} xl={4} className="mb-4">
      <Card className="h-100 border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <Flex justifyContent="between" alignItems="start" className="mb-4">
            <div>
              <h5 className="mb-1 fw-bold fs-9 text-900">Patient Coverage Authentication</h5>
              <p className="mb-0 text-500 fs-11">Real-time NPHIES eligibility verification results</p>
            </div>
            <div
              className="rounded-3 d-flex  align-items-center justify-content-center"
              style={{ width: '32px', height: '32px' }}
            >
              <FontAwesomeIcon icon="check-circle" className="text-success fs-9" />
            </div>
          </Flex>


          <div className="flex-grow-1 position-relative d-flex align-items-center justify-content-center mb-4">
            <BasicECharts
              echarts={echarts}
              options={getOptions(getThemeColor, data, isDark)}
              style={{ width: "16rem", height: "16rem" }}
            />
          </div>

          <div className="mt-4 row g-3">
            {data.map(item => (
              <Col xs={6} key={item.name}>
                <div
                  className="p-2 rounded-3 border border-200 hover-shadow-sm transition-base cursor-default"
                  style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc' }}
                >
                  <div className="d-flex align-items-center gap-1 mb-1">
                    <div
                      className="rounded-circle"
                      style={{ width: '6px', height: '6px', backgroundColor: item.color }}
                    ></div>
                    <span
                      className="text-500 fw-bold text-uppercase tracking-tighter"
                      style={{ fontSize: '10px' }}
                    >
                      {item.name}
                    </span>
                  </div>
                  <p className="mb-0 fs--1 fw-bold text-800">{item.value.toLocaleString()}</p>
                </div>
              </Col>
            ))}
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default TotalRateEligibility;
